"use client"

import { useState } from "react";
import { track } from '@vercel/analytics';
import { buttons, typography } from "@/styles/design-tokens";

const INIT = "INIT";
const SUBMITTING = "SUBMITTING";
const ERROR = "ERROR";
const SUCCESS = "SUCCESS";

const formStyles = {
  id: "cm8hedbmt016m3ddrake29hcx",
  name: "Default",
  formStyle: "buttonBelow",
  namePlaceholder: "First name's fine",
  emailPlaceholder: "Enter your email",
  buttonText: "Subscribe to our newsletter",
  userGroup: "Website",
  successMessage: "Thanks! We&apos;ll be in touch!",
};

const domain = "app.loops.so";

export default function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [formState, setFormState] = useState<typeof INIT | typeof SUBMITTING | typeof ERROR | typeof SUCCESS>(INIT);
  const [errorMessage, setErrorMessage] = useState("");
  const [fields, setFields] = useState<{ [key: string]: string }>({});

  const resetForm = () => {
    setEmail("");
    setFirstName("");
    setFormState(INIT);
    setErrorMessage("");
    setFields({});
  };

  const hasRecentSubmission = () => {
    const timestampNow = Date.now();
    const previousTimestamp = localStorage.getItem("loops-form-timestamp");

    if (
      previousTimestamp &&
      Number(previousTimestamp) + 60 * 1000 > timestampNow
    ) {
      setFormState(ERROR);
      setErrorMessage("Too many signups, please try again in a little while");
      track('signup_rate_limited');
      return true;
    }
    localStorage.setItem("loops-form-timestamp", timestampNow.toString());
    return false;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (formState !== INIT) return;

    if (!isValidEmail(email)) {
      setFormState(ERROR);
      setErrorMessage("Please enter a valid email");
      track("signup_invalid_email");
      return;
    }
    if (hasRecentSubmission()) return;

    setFormState(SUBMITTING);

    // Honeypot check
    if (fields.company) {
      setFormState(ERROR);
      setErrorMessage("Spam detected.");
      track("signup_spam_detected");
      return;
    }

    const additionalFields = Object.entries(fields).reduce(
      (acc, [key, val]) =>
        val ? acc + "&" + key + "=" + encodeURIComponent(val) : acc,
      ""
    );

    const formBody = `userGroup=${encodeURIComponent(
      formStyles.userGroup
    )}&email=${encodeURIComponent(email)}&firstName=${encodeURIComponent(
      firstName
    )}&mailingLists=` + additionalFields;

    fetch(`https://${domain}/api/newsletter-form/${formStyles.id}`, {
      method: "POST",
      body: formBody,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })
      .then(async (res) => {
        if (res.ok) {
          resetForm();
          setFormState(SUCCESS);
          track("signup_success");
        } else {
          const data = await res.json();
          setFormState(ERROR);
          setErrorMessage(data.message || res.statusText);
          localStorage.setItem("loops-form-timestamp", "");
          track("signup_error");
        }
      })
      .catch((error) => {
        setFormState(ERROR);
        if (error.message === "Failed to fetch") {
          setErrorMessage("Too many signups, please try again in a little while");
        } else if (error.message) {
          setErrorMessage(error.message);
        }
        localStorage.setItem("loops-form-timestamp", "");
        track("signup_error");
      });
  };

  if (formState === SUCCESS) {
    return (
      <section aria-live="polite" role="alert">
        <p className={`${typography.caption} text-center w-full`}>{formStyles.successMessage}</p>
        <button type="button" onClick={resetForm} className={`${buttons.secondarySmall} mx-auto mt-4`}>
          Subscribe another email
        </button>
      </section>
    );
  }

  if (formState === ERROR) {
    return (
      <section aria-live="assertive" role="alert" aria-atomic="true">
        <p id="form-error">{errorMessage || "Oops! Something went wrong, please try again"}</p>
        <button type="button" onClick={resetForm}>
          &larr; Back
        </button>
      </section>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      aria-live="polite"
    >
      <fieldset className="flex flex-col gap-4 w-full max-w-5xl mx-auto">
        <legend className="sr-only">Newsletter Signup</legend>

        <div className="rounded-xl border-2 border-black input-wrapper overflow-hidden flex flex-col bg-white md:grid md:grid-cols-2">
          <div className="flex flex-col">
            <label htmlFor="firstName" className={`${typography.caption} px-6 pt-4`}>
              What&apos;s your name? <span aria-hidden="true">*</span>
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              placeholder={formStyles.namePlaceholder}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
              aria-required="true"
              className={`${typography.bodyLarge} px-6 pb-4 pt-3`}
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="email" className={`${typography.caption} px-6 pt-4`}>
              What&apos;s your email? <span aria-hidden="true">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder={formStyles.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              aria-required="true"
              className={`${typography.bodyLarge} px-6 pb-4 pt-3`}
            />
          </div>
        </div>

        {/* Honeypot - hidden field for bot protection */}
        <input
          type="text"
          name="company"
          tabIndex={-1}
          autoComplete="off"
          style={{ position: "absolute", left: "-9999px" }}
          aria-hidden="true"
          onChange={(e) => setFields({ ...fields, company: e.target.value })}
          value={fields.company || ""}
        />

        <button type="submit" className={`${buttons.primary} !w-full`} disabled={formState === SUBMITTING} aria-busy={formState === SUBMITTING}>
          {formState === SUBMITTING ? "Please wait..." : (
            <>
              <span className="hidden sm:block">{formStyles.buttonText}</span>
              <span className="block sm:hidden">Subscribe</span>
            </>
          )}
        </button>
      </fieldset>
    </form>
  );
}

function isValidEmail(email: string) {
  // Simple basic email validation
  return /.+@.+/.test(email);
}
